import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampaignComparison = () => {
  const [selectedMetric, setSelectedMetric] = useState('engagement');

  const campaignData = [
    {
      name: 'Fantasy Novel Launch',
      engagement: 85,
      opens: 1240,
      clicks: 320,
      downloads: 89
    },
    {
      name: 'Author Interview Series',
      engagement: 72,
      opens: 980,
      clicks: 245,
      downloads: 67
    },
    {
      name: 'Book Review Campaign',
      engagement: 91,
      opens: 1450,
      clicks: 410,
      downloads: 112
    },
    {
      name: 'Holiday Promotion',
      engagement: 68,
      opens: 890,
      clicks: 198,
      downloads: 45
    },
    {
      name: 'New Release Announcement',
      engagement: 78,
      opens: 1120,
      clicks: 285,
      downloads: 73
    }
  ];

  const metrics = [
    { key: 'engagement', label: 'Engagement Rate', color: '#1E3A5F', unit: '%' },
    { key: 'opens', label: 'Email Opens', color: '#4A90A4', unit: '' },
    { key: 'clicks', label: 'Click-through', color: '#E67E22', unit: '' },
    { key: 'downloads', label: 'Downloads', color: '#27AE60', unit: '' }
  ];

  const selectedMetricData = metrics?.find(m => m?.key === selectedMetric);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="BarChart3" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Campaign Performance Comparison</h3>
            <p className="text-sm text-muted-foreground">Compare metrics across recent campaigns</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" iconName="Filter">
            Filter
          </Button>
          <Button variant="ghost" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {metrics?.map((metric) => (
            <Button
              key={metric?.key}
              variant={selectedMetric === metric?.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMetric(metric?.key)}
            >
              {metric?.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer>
          <BarChart data={campaignData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="name" 
              stroke="#7F8C8D"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#7F8C8D"
              fontSize={12}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => [
                `${value}${selectedMetricData?.unit || ''}`, 
                selectedMetricData?.label
              ]}
            />
            <Bar 
              dataKey={selectedMetric} 
              fill={selectedMetricData?.color} 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics?.map((metric) => {
          const total = campaignData?.reduce((sum, campaign) => sum + campaign?.[metric?.key], 0);
          const average = Math.round(total / campaignData?.length);
          
          return (
            <div key={metric?.key} className="text-center p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">{metric?.label}</p>
              <p className="text-lg font-semibold text-foreground">
                {average}{metric?.unit}
              </p>
              <p className="text-xs text-muted-foreground">Average</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignComparison;